import { useEffect, useState } from "react";
import {
    Send,
    CheckCircle2,
    XCircle,
    PauseCircle,
    Plus,
    Trash2,
    Loader2,
    Link as LinkIcon,
    Bell,
} from "lucide-react";
import { GateStatusPill } from "./shared";
import api, { formatApiErrorDetail } from "../../lib/api";
import { can, DISABLED_BTN_CLS, permissionTitle } from "../../lib/permissions";
import { toast } from "sonner";

export default function DetailPanel({ gate, user, onChange }) {
    const [links, setLinks] = useState(gate?.deliverable_links || []);
    const [linkInput, setLinkInput] = useState("");
    const [busy, setBusy] = useState(null); // 'submit' | 'approve' | 'links' | 'fail' | 'hold' | 'request'

    const canApprove = can(user, "approveGate");
    const canSubmit = can(user, "submitGateForReview");
    const canUpload = can(user, "uploadDeliverable");
    const canRequest = can(user, "requestApproval");

    useEffect(() => {
        setLinks(gate?.deliverable_links || []);
        setLinkInput("");
    }, [gate?.id, gate?.deliverable_links]);

    if (!gate) {
        return (
            <div
                className="border border-hairline bg-jet-elev1 min-h-[400px] p-12 text-center text-snow-muted text-xs font-mono"
                data-testid="gate-detail-panel"
            >
                Select a gate from the timeline to view its details.
            </div>
        );
    }

    const persistLinks = async (newLinks) => {
        setBusy("links");
        try {
            await api.patch(`/product-gate-instances/${gate.id}`, { deliverable_links: newLinks });
            setLinks(newLinks);
            toast.success("Deliverables updated");
            onChange && onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(null);
        }
    };

    const addLink = async () => {
        const trimmed = linkInput.trim();
        if (!trimmed) return;
        await persistLinks([...links, trimmed]);
        setLinkInput("");
    };

    const removeLink = async (idx) => {
        await persistLinks(links.filter((_, i) => i !== idx));
    };

    const setStatus = async (next, action) => {
        setBusy(action);
        try {
            await api.patch(`/product-gate-instances/${gate.id}`, { status: next });
            toast.success(`Gate set to ${next}`);
            onChange && onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(null);
        }
    };

    const requestApproval = async () => {
        setBusy("request");
        try {
            await api.post(`/product-gate-instances/${gate.id}/request-approval`);
            toast.success("Approval requested — leadership notified.");
            onChange && onChange();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(null);
        }
    };

    return (
        <div
            className="border border-hairline bg-jet-elev1 min-h-[400px] flex flex-col"
            data-testid="gate-detail-panel"
        >
            <div className="border-b border-hairline px-5 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <span className="text-signal font-mono font-bold tabular-nums shrink-0">
                        G{String(gate.gate_id).padStart(2, "0")}
                    </span>
                    <div className="font-mono text-sm truncate">{gate.gate_name}</div>
                </div>
                <GateStatusPill status={gate.status} />
            </div>

            <div className="p-5 space-y-5 overflow-y-auto flex-1">
                <div>
                    <div className="overline mb-2">Pass Criteria</div>
                    <div
                        className="border border-hairline bg-jet p-3 text-sm font-mono text-snow-dim leading-relaxed whitespace-pre-wrap"
                        data-testid="gate-pass-criteria"
                    >
                        {gate.pass_criteria || (
                            <span className="text-snow-muted italic">No criteria documented.</span>
                        )}
                    </div>
                </div>

                <div>
                    <div className="overline mb-2">Required Roles for Approval</div>
                    <div className="flex flex-wrap gap-2" data-testid="gate-required-roles">
                        {(gate.required_roles || []).length === 0 ? (
                            <span className="text-xs text-snow-muted font-mono italic">None</span>
                        ) : (
                            gate.required_roles.map((r) => (
                                <span
                                    key={r}
                                    className="border border-hairline text-snow-dim text-[10px] px-2 py-1 uppercase tracking-wider font-mono"
                                >
                                    {r}
                                </span>
                            ))
                        )}
                    </div>
                </div>

                <div>
                    <div className="overline mb-2">Deliverables</div>
                    <div className="border border-hairline bg-jet">
                        {links.length === 0 ? (
                            <div className="p-4 text-xs text-snow-muted font-mono italic">
                                No deliverables attached yet.
                            </div>
                        ) : (
                            <ul className="divide-y divide-hairline" data-testid="gate-deliverable-list">
                                {links.map((url, idx) => (
                                    <li
                                        key={idx}
                                        className="flex items-center gap-2 px-3 py-2 hover:bg-white/[0.03]"
                                    >
                                        <LinkIcon className="w-3 h-3 text-signal shrink-0" />
                                        <a
                                            href={/^https?:\/\//.test(url) ? url : `https://${url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono text-xs text-snow truncate flex-1 hover:text-signal"
                                        >
                                            {url}
                                        </a>
                                        <button
                                            onClick={() => removeLink(idx)}
                                            disabled={busy === "links" || !canUpload}
                                            title={canUpload ? "Remove" : permissionTitle(user, "uploadDeliverable")}
                                            className={`p-1 ${!canUpload ? "text-snow-muted/40 cursor-not-allowed" : "text-snow-muted hover:text-red-400"}`}
                                            data-testid={`remove-deliverable-${idx}`}
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="flex items-center gap-0 border-t border-hairline">
                            <input
                                data-testid="deliverable-link-input"
                                type="text"
                                value={linkInput}
                                onChange={(e) => setLinkInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), canUpload && addLink())}
                                disabled={!canUpload}
                                placeholder={canUpload ? "https://docs.kitt.io/..." : "Upload restricted by role"}
                                className={`flex-1 bg-transparent border-0 px-3 py-2 text-xs font-mono text-snow focus:outline-none ${!canUpload ? "cursor-not-allowed text-snow-muted" : ""}`}
                            />
                            <button
                                data-testid="add-deliverable-btn"
                                onClick={addLink}
                                disabled={!linkInput.trim() || busy === "links" || !canUpload}
                                title={canUpload ? "Add deliverable" : permissionTitle(user, "uploadDeliverable")}
                                className={`px-3 py-2 text-xs uppercase tracking-wider font-bold border-l transition-colors ${!canUpload ? "border-[#2A2A2A] text-snow-muted cursor-not-allowed" : "border-hairline text-snow-dim hover:text-signal hover:border-signal disabled:opacity-50"}`}
                            >
                                <Plus className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="border-t border-hairline pt-4">
                    <div className="overline mb-3">Actions</div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            data-testid="gate-submit-btn"
                            onClick={() => canSubmit && setStatus("In Progress", "submit")}
                            disabled={busy !== null || gate.status === "Passed" || !canSubmit}
                            title={canSubmit ? "Submit for review" : permissionTitle(user, "submitGateForReview")}
                            className={`cerb-btn-secondary inline-flex items-center gap-2 ${!canSubmit ? DISABLED_BTN_CLS : "disabled:opacity-50"}`}
                        >
                            {busy === "submit" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            Submit for Review
                        </button>
                        <button
                            data-testid="gate-approve-btn"
                            onClick={() => canApprove && setStatus("Passed", "approve")}
                            disabled={busy !== null || !canApprove || gate.status === "Passed"}
                            title={canApprove ? "Approve & pass this gate" : permissionTitle(user, "approveGate")}
                            className={`bg-signal text-jet hover:bg-signal-hover px-6 py-2 transition-colors font-bold uppercase tracking-[0.2em] text-xs inline-flex items-center gap-2 ${!canApprove ? DISABLED_BTN_CLS : "disabled:opacity-40 disabled:cursor-not-allowed"}`}
                        >
                            {busy === "approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                            Approve
                        </button>
                        <button
                            data-testid="gate-fail-btn"
                            onClick={() => canSubmit && setStatus("Failed", "fail")}
                            disabled={busy !== null || !canSubmit}
                            title={canSubmit ? "Mark as failed" : permissionTitle(user, "submitGateForReview")}
                            className={`border border-red-500/50 text-red-400 hover:bg-red-500/10 px-6 py-2 transition-colors font-bold uppercase tracking-[0.2em] text-xs inline-flex items-center gap-2 ${!canSubmit ? DISABLED_BTN_CLS : "disabled:opacity-50"}`}
                        >
                            {busy === "fail" ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                            Fail
                        </button>
                        <button
                            data-testid="gate-hold-btn"
                            onClick={() => canSubmit && setStatus("On Hold", "hold")}
                            disabled={busy !== null || !canSubmit}
                            title={canSubmit ? "Place on hold" : permissionTitle(user, "submitGateForReview")}
                            className={`border border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 px-6 py-2 transition-colors font-bold uppercase tracking-[0.2em] text-xs inline-flex items-center gap-2 ${!canSubmit ? DISABLED_BTN_CLS : "disabled:opacity-50"}`}
                        >
                            {busy === "hold" ? <Loader2 className="w-3 h-3 animate-spin" /> : <PauseCircle className="w-3 h-3" />}
                            Hold
                        </button>
                    </div>
                    {!canApprove && (
                        <div className="text-[10px] text-snow-muted mt-3 font-mono uppercase tracking-wider">
                            Approve action restricted — current role: {user?.role || "anonymous"}
                        </div>
                    )}
                </div>

                <div className="text-[10px] text-snow-muted font-mono uppercase tracking-wider pt-2 border-t border-hairline">
                    Last updated:{" "}
                    <span className="text-snow-dim normal-case tracking-normal">{gate.updated_at}</span>
                </div>
            </div>
        </div>
    );
}

